import { Box, Chip, Grid, Typography } from "@mui/material";
import { Neutrals, Primary, SemanticError, SemanticSuccess, Warning } from "../../../core-components/theme/color-palette/colors";
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getInsightTypeChipStyleAndIcon } from "../helpers/insightsHelper";


interface InsightDetailsHeaderProps {
  insightDetails: any;
  isPublic?: boolean;
}

const InsightDetailsHeader: React.FC<InsightDetailsHeaderProps> = ({ insightDetails, isPublic = false}) => {

  const {
    icon: Icon,
    borderColor,
    backgroundColor,
    textColor,
  } = getInsightTypeChipStyleAndIcon(insightDetails?.insightType || '');


  return (
          <Box sx={{ pl: isPublic ? 0 : 3, pr: 3, pt: 3 }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} sm={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ fontWeight: 500, fontSize: '24px' }}>
                {insightDetails?.name}
              </Typography>
              <Chip
                label={insightDetails?.assetName}
                sx={{
                  ml: 2,
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  fontWeight: 400,
                  fontSize: '14px',
                }}
              />
            </Box>
          </Grid>

          {insightDetails?.averageRecurrenceTime?.value && (
            <Grid item xs={6} md={2}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: `1px solid ${SemanticSuccess[900]}`,
                  borderRadius: 1,
                  minHeight: 50,

                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    paddingLeft: '6px',
                    paddingRight: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    fontWeight: 600,
                    fontSize: "1.2rem",
                    minHeight: 50,
                    height: '100%',
                    backgroundColor: SemanticSuccess[900],
                    color: "#fff",
                  }}
                >
                  {insightDetails?.averageRecurrenceTime?.value}
                  <Typography sx={{ fontWeight: 300, fontSize: "11px", textAlign: "left" }}> {insightDetails?.averageRecurrenceTime?.unit} </Typography>
                </Box>
                <Box
                  sx={{
                    paddingLeft: '6px',
                    paddingRight: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: "12px",
                    color: SemanticSuccess[900],
                    backgroundColor: '#fff',
                    height: '100%',
                    width: '100%',
                    fontWeight: 500,
                    textAlign: 'left',
                  }}
                >
                  Average Recurrence Time
                </Box>
              </Box>
            </Grid>
          )}

          {insightDetails?.timesInsightWasOpened && (
            <Grid item xs={6} md={2}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: `1px solid ${Primary[900]}`,
                  borderRadius: 1,
                  minHeight: 50,
                  color: Primary[900],
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: "1.2rem",
                    minHeight: 50,
                    height: '100%',
                    color: "#fff",
                    backgroundColor: Primary[900],
                  }}
                >
                  {insightDetails?.timesInsightWasOpened}
                </Box>
                <Box
                  sx={{
                    paddingLeft: '6px',
                    paddingRight: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: "12px",
                    color: Primary[900],
                    height: '100%',
                    width: '100%',
                    fontWeight: 500,
                  }}
                >
                  {Number(insightDetails?.timesInsightWasOpened) > 1
                    ? `Times this Alarm was Opened`
                    : `Time this Alarm was Opened`}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>

        <Grid container spacing={1} mt={0}>
          <Grid item sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Chip
              label={
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 400,
                  }}
                >
                  {Icon && <Icon sx={{ color: textColor, fontSize: 14, mr: 1 }} />}
                  {insightDetails?.insightType}
                </div>
              }
              variant="filled"
              sx={{
                backgroundColor,
                color: textColor,
                height: 28,
                borderRadius: 20,
                fontSize: 14,
                paddingLeft: '12px',
                paddingRight: '12px',
                border: `1px solid ${borderColor}`,
              }}
            />
          </Grid>
          {insightDetails?.priority && (
            <Grid item sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                  marginLeft: '5px',
                  marginRight: '5px',
                  color: Neutrals[300]
                }}
              >
                |
              </Box>
            </Grid>
          )}

          <Grid item sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              {insightDetails?.priority?.toLowerCase() === 'high' && (
                <span title="High Priority">
                  <KeyboardArrowUpOutlinedIcon sx={{ color: SemanticError[600], fontSize: 18 }} />
                </span>
              )}
              {insightDetails?.priority?.toLowerCase() === 'medium' && (
                <span title="Medium Priority">
                  <DragHandleIcon sx={{ color: Warning[600], fontSize: 18 }} />
                </span>
              )}
              {insightDetails?.priority?.toLowerCase() === 'low' && (
                <span title="Low Priority">
                  <KeyboardArrowDownOutlinedIcon sx={{ color: SemanticSuccess[600], fontSize: 18 }} />
                </span>
              )}
            </Box>
          </Grid>
          {insightDetails?.insightStatus && (
            <Grid item sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                  marginLeft: '5px',
                  marginRight: '5px',
                  color: Neutrals[300]
                }}
              >
                |
              </Box>
            </Grid>
          )}

          <Grid item sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: 14,
                color:
                  insightDetails?.insightStatus !== 'Closed' ? SemanticError[900] : Primary[900],
                textTransform: 'capitalize',
              }}
            >
              {insightDetails?.insightStatus} Insight </Box>
          </Grid>

          {insightDetails?.timeDiff && (
            <Grid item sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                  marginLeft: '5px',
                  marginRight: '5px',
                  color: Neutrals[300]
                }}
              >
                |
              </Box>
            </Grid>
          )}

          {insightDetails?.timeDiff && (
            <>
              <Grid
                item
                sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon sx={{ color: Neutrals[500], fontSize: "15px" }} />
                  <Box>
                    <Typography variant="body2" sx={{ color: Neutrals[500] }}>{insightDetails?.timeDiff}</Typography>
                  </Box>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
  );
};

export default InsightDetailsHeader;
